import React, { useLayoutEffect, useRef, useState } from 'react'
import { useInView } from 'react-intersection-observer'
import { Link } from 'react-router-dom'

import { theme } from '@acorns/web-components'
import { useRemoteStrings } from '@acorns/web-utils'
import styled from 'styled-components'

import { routes } from 'router/routes'
import { POTENTIAL_DISCLOSURE_LINK } from 'utils/testing/testIDs'

import { RECAP_XS_BREAKPOINT } from '../../styles'
import { AccountValueBreakdown } from './AccountValueBreakdown'
import { PotentialGraph } from './PotentialGraph/PotentialGraph'

const Container = styled.div`
  height: 100%;
  padding: 30px 30px 15px;
  position: relative;
  width: 100%;

  @media screen and (max-width: ${RECAP_XS_BREAKPOINT}px) {
    padding: 0 30px;
  }
`

const Disclaimer = styled.p`
  bottom: 0%;
  color: ${theme.colors.stoneLight};
  font-family: 'Avenir Next';
  font-size: 9.51662px;
  font-style: italic;
  font-weight: 400;
  left: 0;
  line-height: 13px;
  margin: 0;
  padding: 15px 0;
  position: relative;
  text-align: center;
`

const NoSmartDepositDisclaimer = styled.span`
  font-style: italic;
`

const StyledLink = styled(Link)`
  background: none;
  border: 0;
  color: ${theme.colors.stoneLight};
  cursor: pointer;
  font-style: italic;
  padding: 0;
  text-decoration: underline;
`

const PotentialGraphWrapper = styled.div<{ graphTopMargin: number }>`
  margin-top: ${(props) => props.graphTopMargin}px;
  max-width: 340px;
  position: relative;
  width: calc(100vw - 60px);

  @media screen and (max-width: ${RECAP_XS_BREAKPOINT}px) {
    width: 100%;
  }
`

export const Chart = ({
  age,
  ageOfTransfer,
  currentBalance,
  recurringInvestmentSettings,
  smartDepositAmount,
}: {
  age: number
  ageOfTransfer: number
  currentBalance: number
  recurringInvestmentSettings: {
    amount: number
    frequency: string
  }
  smartDepositAmount?: number
}): React.ReactElement => {
  const { getRemoteString } = useRemoteStrings()
  const graphWrapperRef = useRef<HTMLDivElement>(null)
  const [graphTopMargin, setGraphTopMargin] = useState(40)
  const [graphWrapperWidth, setGraphWrapperWidth] = useState(0)
  const { ref, inView } = useInView({
    threshold: 0.5,
    triggerOnce: true,
  })

  const noSmartDeposit = smartDepositAmount === undefined

  useLayoutEffect(() => {
    const resizeUpdate = () => {
      if (graphWrapperRef.current) {
        setGraphWrapperWidth(graphWrapperRef.current.offsetWidth)
      }
    }

    window.addEventListener('resize', resizeUpdate)
    resizeUpdate()
    return () => window.removeEventListener('resize', resizeUpdate)
  }, [])

  useLayoutEffect(() => {
    // adjust for graph height and disclaimer height
    const topMargin =
      (noSmartDeposit ? 260 : 270) -
      (graphWrapperRef.current?.offsetHeight || 0)
    setGraphTopMargin(topMargin)
  }, [graphWrapperRef?.current?.offsetHeight, noSmartDeposit])

  return (
    <Container ref={ref}>
      <AccountValueBreakdown />
      <PotentialGraphWrapper
        ref={graphWrapperRef}
        graphTopMargin={graphTopMargin}
      >
        {graphWrapperWidth && inView ? (
          <PotentialGraph
            age={age}
            ageOfTransfer={ageOfTransfer}
            barWidth={4}
            currentBalance={currentBalance}
            height={260}
            recurringInvestmentSettings={recurringInvestmentSettings}
            smartDepositAmount={smartDepositAmount}
            width={graphWrapperWidth}
          />
        ) : null}
      </PotentialGraphWrapper>
      <Disclaimer>
        {getRemoteString({
          key: 'early.recap.potential.footer_disclosure.statement.1variable',
          variables: ['8'],
        })}{' '}
        {noSmartDeposit ? (
          <NoSmartDepositDisclaimer>
            {getRemoteString({
              key: 'early.recap.potential.footer_disclosure.no_smart_deposit',
            })}{' '}
          </NoSmartDepositDisclaimer>
        ) : null}
        <StyledLink
          aria-label="Go to disclaimer page"
          data-testid={POTENTIAL_DISCLOSURE_LINK}
          to={routes.early.investmentDisclosure}
        >
          {getRemoteString({
            key: 'early.recap.potential.footer_disclosure.disclosure',
          })}
        </StyledLink>
      </Disclaimer>
    </Container>
  )
}
